/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, ListItem, Divider, Drawer, Icon } from '@mui/material';
import { get, map, size } from 'global/lib/lodash';
import useStyles from './styles';
import Skeleton from '@mui/material/Skeleton';
import { useScreen } from 'global/styles';
import { findTaskForItem, getId, isTaskItem } from './index';
import clsx from 'clsx';
import { displayText } from '../../../global/components/ScoreIcon';

const SideBarList = (props) => {
  const { isCurrent, pssvSystem, setSidebarOpen, hash, setHash, setActiveEdit, activeEdit, canShowScores, filter, assessmentTasks } = props;
  const [open, setOpen] = useState(isCurrent);
  const classes = useStyles();

  const toggleOpen = (bool) => {
    setOpen(bool);
  };

  useEffect(() => {
    toggleOpen(isCurrent);
  }, [isCurrent]);

  const onClick = useCallback(
    (c) => {
      setHash(getId(c));
      if (activeEdit && getId(get(activeEdit, 'pssvChecklist')) !== getId(c)) {
        setActiveEdit({
          pssvChecklist: c,
          pssvSystem,
          scroll: true,
          scrollBy: 48,
        });
      }
    },
    [activeEdit]
  );

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let sysVal = useCompareScore ? get(pssvSystem, 'compare.displayValue') : pssvSystem.displayValue;
  if (useCompareScore && useDiff) {
    sysVal = displayText(pssvSystem, pssvSystem.compare, props);
  }

  const tasks = assessmentTasks || [];
  const checklistHasTasks = (checklist) => {
    const children = get(checklist, 'children', []);
    return children.some((cp) => !!findTaskForItem(cp, tasks));
  };

  const systemHasTasks = get(props, 'pssvChecklists', []).some(checklistHasTasks);

  return (
    <>
      <ListItem button className={isCurrent ? classes.systemActive : classes.systemInactive} onClick={() => toggleOpen(!open)}>
        {systemHasTasks && <span className={classes.taskBadge} />}
        <div className={classes.listItemWrapper}>
          <span className={classes.headerChild}>{pssvSystem.name}</span>
          {canShowScores && <span className={clsx(classes.headerChild, classes.sidebarHeaderChild)}>{sysVal}</span>}
          <Icon className={classes.expandIcon}>{open ? 'expand_less' : 'expand_more'}</Icon>
        </div>
      </ListItem>
      <div className={open ? classes.show : classes.hide} onClick={() => setSidebarOpen(false)}>
        {get(props, 'pssvChecklists', []).map((pssvChecklist) => {
          const id = getId(pssvChecklist).toString();
          let checkVal = useCompareScore ? get(pssvChecklist, 'compare.displayValue') : pssvChecklist.displayValue;
          if (useCompareScore && useDiff) {
            checkVal = displayText(pssvChecklist, pssvChecklist.compare, props);
          }
          return (
            <ListItem
              key={id}
              button={true}
              onClick={() => onClick(pssvChecklist)}
              className={clsx(classes.listItemWrapper, classes.listItemWrapChild, hash === id ? classes.checklistActive : classes.checklistInactive)}
            >
              {checklistHasTasks(pssvChecklist) && <span className={classes.taskBadge} />}
              <span className={classes.checklistMenuHeader}>{pssvChecklist.name}</span>
              {canShowScores && <span className={classes.checklistMenuHeader}>{checkVal}</span>}
            </ListItem>
          );
        })}
      </div>
      <Divider />
    </>
  );
};

export default (props) => {
  const { pssvSystems, processedOverallScores, loading, open, setSidebarOpen, setActiveEdit, activeEdit, canShowScores, scorecardIndex, filter, assessmentTasks, currentAnchor } = props;
  const { isMobile } = useScreen();
  const classes = useStyles();
  const [hash, setHash] = useState();
  const userClickedRef = useRef(false);

  const sidebarSetHash = useCallback((h) => {
    userClickedRef.current = true;
    setHash(h);
  }, []);

  const DRAW = isMobile ? Drawer : 'div';

  // Sync sidebar highlight with the currently visible section (from IntersectionObserver)
  useEffect(() => {
    if (currentAnchor && !userClickedRef.current) {
      setHash(currentAnchor);
    }
  }, [currentAnchor]);

  // Handle sidebar click: scroll to the selected section
  useEffect(() => {
    if (hash && userClickedRef.current) {
      userClickedRef.current = false;
      if (!activeEdit) {
        // Ensure body is scrollable (MUI Dialog may leave overflow:hidden via async cleanup)
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView(true);
          window.scrollBy(0, -48);
        }
      }
    }
  }, [hash]);

  useEffect(() => {
    try {
      if (activeEdit) {
        // triggers when user enters Edit Mode
        setHash(getId(get(activeEdit, 'pssvChecklist')));
      } else if (hash) {
        // triggers when user exits Edit Mode: scroll to the item just edited
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView(true);
          window.scrollBy(0, -48);
        }
      }
    } catch (err) {
      console.error(err);
    }
  }, [activeEdit]);

  const useCompareScore = get(filter, 'assessment_status', 'active') !== 'active';
  const useDiff = get(filter, 'useDiff', false);
  let overall = get(processedOverallScores, `${scorecardIndex}.${useCompareScore ? 'compare.displayValue' : 'displayValue'}`);
  if (useCompareScore && useDiff) {
    const thisscore = get(processedOverallScores, scorecardIndex);
    const comp = get(thisscore, 'compare');
    overall = displayText(thisscore, comp, props);
  }

  return (
    <DRAW className={isMobile ? classes.leftSidebarMobile : classes.leftSidebar} open={open} onClose={() => setSidebarOpen(false)}>
      {(loading || !size(pssvSystems)) && <Skeleton variant="rectangular" height={2000} />}
      <List>
        <ListItem className={classes.overallScore}>
          {`Overall:`}
          <span data-testid="overall-score" style={{ marginLeft: 2 }}>
            {overall}
          </span>
        </ListItem>
        {map(pssvSystems, (sys, i) => {
          const isCurrent = hash && (getId(sys).toString() === hash || get(sys, 'children', []).find((ps) => getId(ps).toString() === hash));
          return (
            <SideBarList
              key={i}
              filter={filter}
              pssvSystem={sys}
              pssvChecklists={sys.children}
              setSidebarOpen={setSidebarOpen}
              isCurrent={isCurrent}
              hash={hash}
              setHash={sidebarSetHash}
              setActiveEdit={setActiveEdit}
              activeEdit={activeEdit}
              canShowScores={canShowScores}
              scorecard={props.scorecard}
              assessmentTasks={assessmentTasks}
            />
          );
        })}
      </List>
    </DRAW>
  );
};
